import {
  CarsProperties,
  Coupon,
  GetPaymentProps,
  HomeProperties,
  MotorcicleProperties,
  Payment,
  Price,
  Product,
  Properties,
  subData,
} from '../types/Add-subscription.types';

interface GetPropertiesProps {
  formData: Record<string, Record<string, string>>;
  products: Product[];
  period: 'Mensual' | 'Anual';
  selectedAddons: Record<number, boolean>;
}

export const getProperties = ({
  formData,
  products,
  period,
  selectedAddons,
}: GetPropertiesProps): Properties => {
  const cars: CarsProperties[] = [];
  const motorcycles: MotorcicleProperties[] = [];
  const homes: HomeProperties[] = [];

  Object.entries(formData).forEach(([key, value]) => {
    const [priceId] = key.split('-');
    const price = products
      .flatMap((p) => p.prices)
      .find((p) => p.id === Number(priceId));
    if (price) {
      if (price.product_type_id.name.toLocaleLowerCase() === 'vehiculos') {
        const pricesId = [price.stripe_price_id];
        const priceIdAddon = getPriceIdApplicableAddon({
          productType: 'vehiculos',
          products,
          period,
          selectedAddons,
        });
        if (priceIdAddon.length > 0) pricesId.push(...priceIdAddon);
        cars.push({
          brand: value['Marca'] || '',
          model: value['Modelo'] || '',
          year: Number.parseInt(value['Año']),
          plate: value['Placa'] || '',
          color: value['Color'] || '',
          pricesId,
        });
      } else if (
        price.product_type_id.name.toLocaleLowerCase() === 'motocicleta'
      ) {
        const pricesId = [price.stripe_price_id];
        const priceIdAddon = getPriceIdApplicableAddon({
          productType: 'motocicleta',
          products,
          period,
          selectedAddons,
        });
        if (priceIdAddon.length > 0) pricesId.push(...priceIdAddon);
        motorcycles.push({
          brand: value['Marca'] || '' || '',
          model: value['Modelo'] || '',
          year: Number.parseInt(value['Año']),
          plate: value['Placa'] || '',
          color: value['Color'] || '',
          pricesId,
        });
      } else if (price.product_type_id.name.toLocaleLowerCase() === 'hogar') {
        const pricesId = [price.stripe_price_id];
        const priceIdAddon = getPriceIdApplicableAddon({
          productType: 'hogar',
          products,
          period,
          selectedAddons,
        });
        if (priceIdAddon.length > 0) pricesId.push(...priceIdAddon);
        homes.push({
          address_1: value['Direccion 1'] || '',
          address_2: value['Direccion 2'] || '',
          country: value['Pais'] || '',
          city: value['Ciudad'] || '',
          zip_code: value['Codigo Zip'] || '',
          pricesId,
        });
      }
    }
  });

  return { cars, motorcycles, homes };
};

interface GetSubProps {
  products: Product[];
  period: 'Mensual' | 'Anual';
  counts: Record<number, number>;
  appliedCoupon: Coupon | null;
  selectedAddons: Record<number, boolean>;
  customerAuthId: string;
  agentAuthId: string;
}
export const getSub = ({
  agentAuthId,
  customerAuthId,
  products,
  period,
  counts,
  appliedCoupon,
  selectedAddons,
}: GetSubProps): subData => {
  const purchaseItems: subData['products'] = [];

  // Add main products
  products.forEach((product) => {
    if (!product.is_addon) {
      product.prices.forEach((price) => {
        if (price.periocity_id.name === period && counts[price.id] > 0) {
          purchaseItems.push({
            priceId: price.stripe_price_id,
            quantity: counts[price.id],
            coupon:
              price.id === appliedCoupon?.price.id
                ? appliedCoupon.coupon_code
                : null,
          });
        }
      });
    }
  });

  // Add addons products
  const availableAddons = getAvailableAddons({ products, period, counts });

  // TODO: Refactorizar esta función
  availableAddons.forEach(({ price }) => {
    if (selectedAddons[price.id]) {
      const applicableProductCounts = Object.entries(counts)
        .filter(([countPriceId]) => {
          const countPrice = products
            .flatMap((p) => p.prices)
            .find((p) => p.id === Number(countPriceId));
          return (
            countPrice &&
            countPrice.product_type_id.name === price.product_type_id.name
          );
        })
        .reduce((total, [, count]) => total + count, 0);

      purchaseItems.push({
        priceId: price.stripe_price_id,
        quantity: applicableProductCounts,
        coupon: null,
      });
    }
  });

  return {
    agent_id: agentAuthId,
    customer: customerAuthId,
    products: purchaseItems,
  };
};

interface GetAvailableAddonsReturn {
  product: Product;
  price: Price;
}

interface GetAvailableAddonsProps {
  products: Product[];
  period: 'Mensual' | 'Anual';
  counts: Record<number, number>;
}

const getAddons = (products: Product[]): Product[] => {
  return products.filter((product) => product.is_addon);
};

const getAddonPrices = (
  addons: Product[],
  period: 'Mensual' | 'Anual'
): GetAvailableAddonsReturn[] => {
  return addons.flatMap((product) =>
    product.prices
      .filter((price) => price.periocity_id.name === period)
      .map((price) => ({ product, price }))
  );
};

const hasRelatedProduct = (
  products: Product[],
  price: Price,
  period: 'Mensual' | 'Anual',
  counts: Record<number, number>
): boolean => {
  return products
    .filter((product) => !product.is_addon)
    .some((product) =>
      product.prices.some(
        (pPrice) =>
          pPrice.product_type_id.name === price.product_type_id.name &&
          counts[pPrice.id] > 0 &&
          pPrice.periocity_id.name === period
      )
    );
};

export const getAvailableAddons = ({
  products,
  period,
  counts,
}: GetAvailableAddonsProps): GetAvailableAddonsReturn[] => {
  const addons = getAddons(products);
  const addonPrices = getAddonPrices(addons, period);
  return addonPrices.filter(({ price }) =>
    hasRelatedProduct(products, price, period, counts)
  );
};

export const getPayment = ({ user, cardId }: GetPaymentProps): Payment => {
  try {
    if (!cardId) throw new Error('cardId is required');
    const tknFirebase = user.tknFirebase;
    const customerAuthId = user.uid;
    return { tknFirebase, customerAuthId, cardId };
  } catch (error) {
    console.error('Error getPayment 🚨', error);
    throw error;
  }
};

interface GetApplicableAddonPriceIdsProps {
  products: Product[];
  period: 'Mensual' | 'Anual';
  selectedAddons: Record<number, boolean>;
  productType: string;
}

const getPriceIdApplicableAddon = ({
  productType,
  products,
  selectedAddons,
  period,
}: GetApplicableAddonPriceIdsProps) => {
  return products
    .filter((product) => product.is_addon)
    .flatMap((product) => product.prices)
    .filter(
      (price) =>
        selectedAddons[price.id] &&
        price.product_type_id.name.toLowerCase() ===
          productType.toLowerCase() &&
        price.periocity_id.name === period
    )
    .map((price) => price.stripe_price_id);
};
